import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import InterviewSession from "./pages/InterviewSession";
import SessionHistory from "./pages/SessionHistory";
import Pricing from "./pages/Pricing";
import ResourceHub from "./pages/ResourceHub";
import FAQ from "./pages/FAQ";
import ResumeBuilder from "./pages/ResumeBuilder";
import ResumeNew from "./pages/ResumeNew";
import ResumeImport from "./pages/ResumeImport";
import LinkedInOptimizer from "./pages/LinkedInOptimizer";
import CoverLetterGenerator from "./pages/CoverLetterGenerator";
import PhoneInterviewComingSoon from "./pages/PhoneInterviewComingSoon";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Admin from "./pages/Admin";
import Referral from "./pages/Referral";
import HeadshotGenerator from "./pages/HeadshotGenerator";
import DownloadWindows from "./pages/DownloadWindows";
import DownloadMac from "./pages/DownloadMac";
import Layout from "./components/Layout";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function PremiumProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  const hasPremium = user?.plan === "pro" || user?.plan === "elite" || user?.plan === "admin" || user?.role === "admin";
  if (!hasPremium) return <Navigate to="/pricing" replace state={{ reason: "premium_required" }} />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="/app" element={<Protected><Layout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="sessions" element={<SessionHistory />} />
            <Route path="sessions/:id" element={<InterviewSession />} />
            <Route path="resources" element={<ResourceHub />} />
            <Route path="faq" element={<FAQ />} />
            <Route path="resume" element={<PremiumProtected><ResumeBuilder /></PremiumProtected>} />
            <Route path="resume/new" element={<PremiumProtected><ResumeNew /></PremiumProtected>} />
            <Route path="resume/import" element={<PremiumProtected><ResumeImport /></PremiumProtected>} />
            <Route path="linkedin" element={<PremiumProtected><LinkedInOptimizer /></PremiumProtected>} />
            <Route path="coverletter" element={<PremiumProtected><CoverLetterGenerator /></PremiumProtected>} />
            <Route path="phone" element={<PhoneInterviewComingSoon />} />
            <Route path="referral" element={<Referral />} />
            <Route path="headshot" element={<PremiumProtected><HeadshotGenerator /></PremiumProtected>} />
          <Route path="admin" element={<Protected admin><Admin /></Protected>} />
          </Route>

          <Route path="/download/windows" element={<DownloadWindows />} />
          <Route path="/download/mac" element={<DownloadMac />} />

          {/* Legacy /admin redirect */}
          <Route path="/admin" element={<Navigate to="/app/admin" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
